import React from "react";
import useSWR from "swr";
import { Check, Printer } from "lucide-react";
import Header from "@/components/Header";
import { Card, Button } from "@/components/ui-kit";
import { getAllergenMatrix } from "@/lib/api";

export default function AllergenMatrix() {
  const { data, isLoading } = useSWR("allergen-matrix", getAllergenMatrix);
  const allergens = data?.allergens || [];
  const rows = data?.rows || [];

  const printMatrix = () => {
    const dateLabel = new Date().toLocaleDateString("ca-ES", { day: "numeric", month: "long", year: "numeric" });
    const esc = (s) => String(s).replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
    const th = allergens.map((a) => `<th>${esc(a.name)}</th>`).join("");
    const trs = rows.map((r) => {
      const set = new Set(r.allergens);
      const tds = allergens.map((a) => `<td class="${set.has(a.id) ? "x" : ""}">${set.has(a.id) ? "✕" : ""}</td>`).join("");
      return `<tr><td class="name">${esc(r.name)}</td>${tds}</tr>`;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Matriu al·lèrgens</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font-family: Arial, Helvetica, sans-serif; color:#1E120D; }
        h1 { font-size:18px; margin:0; }
        .sub { color:#695D59; font-size:11px; margin:2px 0 12px; }
        table { border-collapse: collapse; width:100%; font-size:10px; }
        th,td { border:1px solid #C9BDB6; padding:4px 5px; text-align:center; }
        td.name { text-align:left; font-weight:600; white-space:nowrap; }
        thead th { background:#F0E9E4; }
        td.x { background:#B4241A; color:#fff; font-weight:bold; }
      </style></head><body>
      <h1>Matriu d'al·lèrgens — Sublims</h1>
      <div class="sub">Pla 7 · Informació al consumidor · ${dateLabel}</div>
      <table><thead><tr><th class="name" style="text-align:left">Producte</th>${th}</tr></thead><tbody>${trs}</tbody></table>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  return (
    <>
      <Header title="Al·lèrgens" />
      <div className="mx-auto max-w-5xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up">
          <div className="label-eyebrow">Pla 7 · Informació al consumidor</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-ink sm:text-4xl">Matriu d'al·lèrgens</h2>
          <p className="mt-2 text-sm text-ink-soft">
            Es calcula automàticament a partir dels ingredients de cada fitxa de producció.
          </p>
          {rows.length > 0 && (
            <Button variant="secondary" onClick={printMatrix} testid="matrix-print" className="mt-4">
              <Printer size={16} /> Imprimir / PDF
            </Button>
          )}
        </div>

        {isLoading && <Card className="mt-6 text-center text-sm text-ink-soft">Carregant…</Card>}
        {!isLoading && rows.length === 0 && (
          <Card className="mt-6 text-center text-sm text-ink-soft">Encara no hi ha fitxes de producció.</Card>
        )}
        {!isLoading && rows.length > 0 && (
          <div className="mt-6 overflow-x-auto rounded-2xl border border-line bg-white shadow-card">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-muted px-4 py-3 text-left font-display text-ink">Producte</th>
                  {allergens.map((a) => (
                    <th key={a.id} className="whitespace-nowrap border-l border-line bg-muted px-3 py-3 text-center text-xs font-semibold text-ink-soft">
                      {a.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const set = new Set(r.allergens);
                  return (
                    <tr key={r.sheet_id} className="border-t border-line">
                      <td className="sticky left-0 z-10 bg-white px-4 py-3 font-semibold text-ink">{r.name}</td>
                      {allergens.map((a) => (
                        <td key={a.id} className="border-l border-line px-3 py-3 text-center">
                          {set.has(a.id) ? (
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-ember text-white"><Check size={14} /></span>
                          ) : (
                            <span className="text-line">·</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-4 text-xs text-ink-soft">
          <span className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-ember align-middle text-white"><Check size={10} /></span>
          = conté l'al·lergen. Cel·les buides = no conté (segons ingredients declarats).
        </p>
      </div>
    </>
  );
}
